import { Injectable, inject, signal } from '@angular/core';
import { CourseService } from '../../../../services/course.service';

/**
 * Service for managing script loading state
 * Follows Single Responsibility Principle - only handles script state
 */
@Injectable()
export class ScriptStateService {
  private courseService = inject(CourseService);

  // Store fetched scripts by task ID
  private scripts = signal<Map<string, string>>(new Map());
  private scriptLoading = signal<Set<string>>(new Set());
  private scriptErrors = signal<Set<string>>(new Set());

  /**
   * Gets the script for a task ID
   */
  getScript(taskId: string): string | null {
    return this.scripts().get(taskId) || null;
  }

  /**
   * Checks if a script is currently loading
   */
  isScriptLoading(taskId: string): boolean {
    return this.scriptLoading().has(taskId);
  }

  /**
   * Checks if there was an error loading a script
   */
  hasScriptError(taskId: string): boolean {
    return this.scriptErrors().has(taskId);
  }

  /**
   * Fetches a script from a URL and stores it
   */
  async fetchScript(taskId: string, scriptUrl: string): Promise<void> {
    // Add to loading set
    const loading = new Set(this.scriptLoading());
    loading.add(taskId);
    this.scriptLoading.set(loading);

    // Remove from errors if previously failed
    const errors = new Set(this.scriptErrors());
    errors.delete(taskId);
    this.scriptErrors.set(errors);

    try {
      const scriptText = await this.courseService.fetchScript(scriptUrl);

      const scripts = new Map(this.scripts());
      scripts.set(taskId, scriptText);
      this.scripts.set(scripts);
    } catch (error) {
      const errorSet = new Set(this.scriptErrors());
      errorSet.add(taskId);
      this.scriptErrors.set(errorSet);
    } finally {
      const loadingSet = new Set(this.scriptLoading());
      loadingSet.delete(taskId);
      this.scriptLoading.set(loadingSet);
    }
  }

  /**
   * Clears all script state (useful for cleanup)
   */
  clear(): void {
    this.scripts.set(new Map());
    this.scriptLoading.set(new Set());
    this.scriptErrors.set(new Set());
  }
}
